import { logError, logWarn } from '@/common/helpers';

const DB_NAME = 'flm_fun';
const DB_VERSION = 10;

// Cache version - increment this to force clear all cached data on next deploy
// This is separate from DB_VERSION and will nuke everything when changed
const CACHE_VERSION = 1;
const CACHE_VERSION_KEY = 'flm_cache_version';

export const STORE_PREDICTIONS = 'predictions';
export const STORE_CREATOR_MARKETS = 'creator_markets';
export const STORE_BLOBS = 'blobs';
export const STORE_NOTIFICATIONS = 'notifications';
export const STORE_PLAY_PREDICTIONS = 'play_predictions';
export const STORE_PLAY_KV = 'play_kv';
export const STORE_MARKET_DRAFTS = 'market_drafts';

export const PRED_IDX_USER = 'by_userId';
export const PRED_IDX_MARKET = 'by_marketId';
export const PRED_IDX_USER_MARKET = 'by_user_marketId';
export const PRED_IDX_IS_COMPLETE = 'by_user_isComplete';

export const CREATOR_IDX_CREATOR = 'by_creatorId';
export const CREATOR_IDX_CREATOR_COMPLETE = 'by_creator_isComplete';
export const CREATOR_IDX_CREATOR_UPDATED = 'by_creator_updated';

export const NOTIF_IDX_USER = 'by_user';
export const NOTIF_IDX_USER_DATE = 'by_user_date';

export function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;

      if (!db.objectStoreNames.contains(STORE_PREDICTIONS)) {
        const store = db.createObjectStore(STORE_PREDICTIONS, { keyPath: 'id' });
        store.createIndex(PRED_IDX_USER, 'userId', { unique: false });
        store.createIndex(PRED_IDX_MARKET, 'marketId', { unique: false });
        store.createIndex(PRED_IDX_USER_MARKET, ['userId', 'marketId'], { unique: false });
        store.createIndex(PRED_IDX_IS_COMPLETE, ['userId', 'isComplete'], { unique: false });
      }

      if (!db.objectStoreNames.contains(STORE_PLAY_PREDICTIONS)) {
        const store = db.createObjectStore(STORE_PLAY_PREDICTIONS, { keyPath: 'id' });
        store.createIndex(PRED_IDX_USER, 'userId', { unique: false });
        store.createIndex(PRED_IDX_MARKET, 'marketId', { unique: false });
        store.createIndex(PRED_IDX_USER_MARKET, ['userId', 'marketId'], { unique: false });
      }

      if (!db.objectStoreNames.contains(STORE_PLAY_KV)) {
        db.createObjectStore(STORE_PLAY_KV);
      }

      if (!db.objectStoreNames.contains(STORE_CREATOR_MARKETS)) {
        const store = db.createObjectStore(STORE_CREATOR_MARKETS, {
          keyPath: 'id',
        });

        store.createIndex(CREATOR_IDX_CREATOR, 'creatorId', { unique: false });
        store.createIndex(CREATOR_IDX_CREATOR_COMPLETE, ['creatorId', 'isComplete'], {
          unique: false,
        });
        store.createIndex(CREATOR_IDX_CREATOR_UPDATED, ['creatorId', 'lastUpdatedAtMs'], {
          unique: false,
        });
      }

      if (!db.objectStoreNames.contains(STORE_BLOBS)) {
        db.createObjectStore(STORE_BLOBS, { keyPath: 'id' });
      }

      if (!db.objectStoreNames.contains(STORE_NOTIFICATIONS)) {
        const store = db.createObjectStore(STORE_NOTIFICATIONS, { keyPath: 'id' });

        store.createIndex(NOTIF_IDX_USER, 'userId', { unique: false });
        store.createIndex(NOTIF_IDX_USER_DATE, ['userId', 'dateMs'], { unique: false });
      }

      // Market drafts store - stores draft markets per user
      // Key format: `{userAddress}_{draftId}`
      if (!db.objectStoreNames.contains(STORE_MARKET_DRAFTS)) {
        db.createObjectStore(STORE_MARKET_DRAFTS, { keyPath: 'id' });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Failed to open IndexedDB'));
  });
}

/**
 * Check if cache version matches current version.
 * If not, nuke all cached data and update version.
 * Call this on app startup before any DB operations.
 */
export async function checkAndClearCacheIfNeeded(): Promise<boolean> {
  try {
    const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    const currentVersion = CACHE_VERSION.toString();

    if (storedVersion !== currentVersion) {
      logWarn('localDb', `cache version mismatch (stored: ${storedVersion}, current: ${currentVersion}). Clearing all cached data...`);

      // Delete the entire database to clear all stores
      await new Promise<void>((resolve, reject) => {
        const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
        deleteRequest.onsuccess = () => {
          logWarn('localDb', 'IndexedDB deleted successfully');
          resolve();
        };
        deleteRequest.onerror = () => {
          logError('localDb', 'deleting IndexedDB', deleteRequest.error);
          reject(deleteRequest.error);
        };
        deleteRequest.onblocked = () => {
          logWarn('localDb', 'IndexedDB deletion blocked - close all tabs and try again');
        };
      });

      // Clear localStorage cache-related items (but keep user preferences)
      const itemsToKeep = ['selectedAccountId', 'accountProvider'];
      const allKeys = Object.keys(localStorage);
      allKeys.forEach((key) => {
        if (!itemsToKeep.includes(key) && key !== CACHE_VERSION_KEY) {
          localStorage.removeItem(key);
        }
      });

      // Update stored version
      localStorage.setItem(CACHE_VERSION_KEY, currentVersion);

      logWarn('localDb', 'cache cleared successfully. Page will reload.');
      return true; // Indicates cache was cleared
    }

    return false; // No clearing needed
  } catch (error) {
    logError('localDb', 'checking cache version', error);
    // Update version anyway to prevent infinite loops
    localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION.toString());
    return false;
  }
}

export default {
  openDb,
  checkAndClearCacheIfNeeded,
  STORE_PREDICTIONS,
  STORE_CREATOR_MARKETS,
  STORE_BLOBS,
  STORE_NOTIFICATIONS,
  STORE_PLAY_PREDICTIONS,
  STORE_PLAY_KV,
  STORE_MARKET_DRAFTS,
  PRED_IDX_USER,
  PRED_IDX_MARKET,
  PRED_IDX_USER_MARKET,
  PRED_IDX_IS_COMPLETE,
  CREATOR_IDX_CREATOR,
  CREATOR_IDX_CREATOR_COMPLETE,
  CREATOR_IDX_CREATOR_UPDATED,
  NOTIF_IDX_USER,
  NOTIF_IDX_USER_DATE,
};
